// src/app/features/dashboard/widgets/ScarletWell/CheckIn/ScarletWellCheckIn.js
import React from 'react'
import { Box, Button, Card, Typography, Divider } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  fetchScarletWellCheckInData,
  selectScarletWellData,
  selectScarletWellStatus,
  submitAllAnswers,
} from '../scarletWellSlice'
import BackButtonNavBar from '../../../../../CommonComponents/BackButtonNavBar/BackButtonNavBar'
import Modal from '../../../../../CommonComponents/Modal/Modal'
import Loading from '../../helpers/Loading'
import { ErrorMessage } from '../../../DashboardComponents/ErrorMessages/ErrorMsg'
import useScarletWellCheckInStyles from '../ScarletWellCheckInStyles'
import {
  getSortedResponseOptions,
  getResponseLabel,
  getAnswerScaleLayoutStyle,
  getCategoryLabel,
  getIncompleteCategoryIds,
  handleCloseModal as handleCloseModalHelper,
  handleScrollToTop as handleScrollToTopHelper,
  onPreviousCategory as onPreviousCategoryHelper,
  onNextCategory as onNextCategoryHelper,
  handleSubmit as handleSubmitHelper,
} from './checkInHelpers'

// TODO: Extract this into firebase
const INTRO_COPY =
  'This inventory gives you a way of thinking about what you are doing, and how you are feeling in general. When you read each item, think about the things you have been doing for your personal wellness during the past 30 days.'

const SCALE_COPY =
  'Answer each item in a way that best captures how often the statement is true for you.'

function renderPageState(
  status,
  surveyData,
  selectedCategoryId,
  selectedAnswers,
  invalidCategoryIds,
  hasAttemptedSubmit,
  isSubmitting,
  classes,
  setSelectedCategoryId,
  setSelectedAnswers,
  onPreviousCategory,
  onNextCategory,
  onSubmit,
) {
  switch (status) {
    case 'fulfilled':
      return (
        <SurveyContent
          surveyData={surveyData}
          selectedCategoryId={selectedCategoryId}
          selectedAnswers={selectedAnswers}
          invalidCategoryIds={invalidCategoryIds}
          hasAttemptedSubmit={hasAttemptedSubmit}
          isSubmitting={isSubmitting}
          classes={classes}
          setSelectedCategoryId={setSelectedCategoryId}
          setSelectedAnswers={setSelectedAnswers}
          onPreviousCategory={onPreviousCategory}
          onNextCategory={onNextCategory}
          onSubmit={onSubmit}
        />
      )
    case 'error':
      return (
        <Box className={classes.stateContainer}>
          <ErrorMessage />
        </Box>
      )
    case 'pending':
    default:
      return (
        <Box className={classes.stateContainer}>
          <Loading />
        </Box>
      )
  }
}

function SurveyContent(props) {
  const {
    surveyData,
    selectedCategoryId,
    selectedAnswers,
    invalidCategoryIds,
    hasAttemptedSubmit,
    isSubmitting,
    classes,
    setSelectedCategoryId,
    setSelectedAnswers,
    onPreviousCategory,
    onNextCategory,
    onSubmit,
  } = props
  const theme = useTheme()
  const isCompactScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // Memoize categories to prevent returning a new array reference on every render
  const categories = React.useMemo(
    () => surveyData?.checkInPage?.categories || [],
    [surveyData?.checkInPage?.categories],
  )

  // Memoize sorted response options to prevent re-sorting on every render
  const responseOptions = React.useMemo(
    () =>
      getSortedResponseOptions(surveyData?.checkInPage?.responseOptions || []),
    [surveyData?.checkInPage?.responseOptions],
  )

  const answerScaleLayoutStyle = React.useMemo(
    () =>
      isCompactScreen ? getAnswerScaleLayoutStyle(responseOptions) : undefined,
    [isCompactScreen, responseOptions],
  )

  const activeCategory = React.useMemo(
    () =>
      categories.find(
        (category) => category.categoryId === selectedCategoryId,
      ) || categories[0],
    [categories, selectedCategoryId],
  )

  const activeCategoryId = activeCategory ? activeCategory.categoryId : null
  const activeCategoryIndex = categories.findIndex(
    (category) => category.categoryId === activeCategoryId,
  )
  const showPreviousButton = activeCategoryIndex > 0
  const showNextButton =
    activeCategoryIndex > -1 && activeCategoryIndex < categories.length - 1

  const answers = React.useMemo(() => {
    if (!responseOptions || responseOptions.length === 0) return ''
    return responseOptions.map((option) => option.responseTitle).join(' - ')
  }, [responseOptions])

  const recentSubmissionDate =
    surveyData?.checkInPage?.redCapSubmissions?.[0]?.date
  const isRecentSubmission = React.useMemo(() => {
    if (!recentSubmissionDate) return false
    const submissionTime = new Date(recentSubmissionDate).getTime()
    const now = new Date().getTime()
    const hoursDifference = (now - submissionTime) / (1000 * 60 * 60)
    return hoursDifference >= 0 && hoursDifference < 24
  }, [recentSubmissionDate])

  return (
    <Box className={classes.surveyShell}>
      <Card className={classes.introCard} elevation={0}>
        <Box className={classes.introHeader}>
          <Typography variant="h2" className={classes.introHeaderText}>
            Wellness Inventory
          </Typography>
        </Box>
        <Box className={classes.introContent}>
          <Typography variant="h6" className={classes.introParagraph}>
            {INTRO_COPY}
          </Typography>
          <Typography variant="subtitle1" className={classes.introSubText}>
            {SCALE_COPY}
          </Typography>
          <Typography variant="h6" className={classes.introScaleText}>
            {answers}
          </Typography>
          <Divider className={classes.introDivider} />
        </Box>
      </Card>

      <Box className={classes.categoryPanel}>
        {isRecentSubmission && (
          <Box className={classes.categoryWarningBanner} role="alert">
            <Typography variant="body1" className={classes.categoryWarningText}>
              Please wait 24 hours before submitting another Wellness Inventory
              survey
            </Typography>
          </Box>
        )}
        {!isRecentSubmission &&
          hasAttemptedSubmit &&
          invalidCategoryIds.length > 0 && (
            <Box className={classes.categoryWarningBanner} role="alert">
              <Typography
                variant="body1"
                className={classes.categoryWarningText}
              >
                Categories highlighted in red need to be completed before
                submission
              </Typography>
            </Box>
          )}
        <Box className={classes.categoryGridWrapper}>
          <Box className={classes.categoryGrid}>
            {categories.map((category) => {
              const isActive = category.categoryId === activeCategoryId
              const isInvalid =
                hasAttemptedSubmit &&
                invalidCategoryIds.includes(category.categoryId)

              return (
                <Button
                  key={category.categoryId}
                  className={`${classes.categoryButton} ${
                    isActive ? classes.activeCategoryButton : ''
                  } ${isInvalid ? classes.invalidCategoryButton : ''}`}
                  onClick={() => setSelectedCategoryId(category.categoryId)}
                  aria-invalid={isInvalid ? 'true' : undefined}
                >
                  <Typography
                    variant="h6"
                    className={`${classes.categoryButtonText} ${
                      isActive ? classes.activeCategoryButtonText : ''
                    }`}
                  >
                    {getCategoryLabel(category)}
                  </Typography>
                </Button>
              )
            })}
          </Box>
        </Box>
      </Box>

      <Box className={classes.questionTable}>
        <Box className={classes.questionTableHeader}>
          <Typography variant="h4" className={classes.tableHeaderLabel}>
            Questions
          </Typography>
          <Typography
            variant="h4"
            className={`${classes.tableHeaderLabel} ${classes.tableHeaderLabelRight}`}
          >
            Answers
          </Typography>
        </Box>

        <Box className={classes.answerScaleHeader}>
          <Box />
          <Box
            className={classes.answerScaleLabels}
            style={answerScaleLayoutStyle}
          >
            {responseOptions.map((responseOption) => (
              <Typography
                variant="body1"
                key={responseOption.responseId}
                className={classes.answerScaleLabel}
              >
                {getResponseLabel(responseOption)}
              </Typography>
            ))}
          </Box>
        </Box>

        {activeCategory && activeCategory.questions.length > 0 ? (
          activeCategory.questions.map((question, index) => (
            <Box
              key={question.questionId}
              className={`${classes.questionRow} ${
                index % 2 === 1 ? classes.questionRowAlternate : ''
              }`}
              role="radiogroup"
              aria-labelledby={`question-label-${question.questionId}`}
            >
              <Typography
                id={`question-label-${question.questionId}`}
                className={classes.questionText}
                variant="body1"
              >
                {question.questionText}
              </Typography>
              <Box
                className={classes.answerScale}
                style={answerScaleLayoutStyle}
              >
                {responseOptions.map((responseOption) => {
                  const isChecked =
                    selectedAnswers[question.questionId] ===
                    responseOption.responseId

                  return (
                    <label
                      key={responseOption.responseId}
                      className={`${classes.answerCell} ${
                        isChecked ? classes.answerCellActive : ''
                      }`}
                    >
                      <input
                        aria-label={`${question.questionText} ${responseOption.responseTitle}`}
                        checked={isChecked}
                        className={classes.hiddenInput}
                        name={`question-${question.questionId}`}
                        onChange={() =>
                          setSelectedAnswers((currentAnswers) => ({
                            ...currentAnswers,
                            [question.questionId]: responseOption.responseId,
                          }))
                        }
                        type="radio"
                        value={responseOption.responseId}
                      />
                      <span
                        className={`${classes.radioOuter} ${
                          isChecked ? classes.radioOuterChecked : ''
                        }`}
                      >
                        <span
                          className={`${classes.radioInner} ${
                            isChecked ? classes.radioInnerChecked : ''
                          }`}
                        />
                      </span>
                      <Typography
                        component="span"
                        variant="caption"
                        className={classes.mobileOptionLabel}
                      >
                        {getResponseLabel(responseOption)}
                      </Typography>
                    </label>
                  )
                })}
              </Box>
            </Box>
          ))
        ) : (
          <Box className={classes.emptyState}>
            <Typography variant="body1">
              No questions are available for this category yet.
            </Typography>
          </Box>
        )}
      </Box>

      <Box className={classes.mobileFooterActions}>
        <Box className={classes.mobileFooterSide}>
          {showPreviousButton ? (
            <Button
              className={classes.mobileNavButton}
              onClick={onPreviousCategory}
            >
              <Typography variant="h6" className={classes.buttonText}>
                Previous
              </Typography>
            </Button>
          ) : null}
        </Box>
        <Box className={classes.mobileFooterSideRight}>
          {showNextButton ? (
            <Button
              className={classes.mobileNavButton}
              onClick={onNextCategory}
            >
              <Typography variant="h6" className={classes.buttonText}>
                Next
              </Typography>
            </Button>
          ) : null}
          <Button
            className={
              isSubmitting || isRecentSubmission
                ? classes.mobileSubmitButtonDisabled
                : classes.mobileSubmitButton
            }
            onClick={isSubmitting || isRecentSubmission ? undefined : onSubmit}
            disabled={isSubmitting || isRecentSubmission}
          >
            <Typography
              variant="h6"
              className={
                isSubmitting || isRecentSubmission
                  ? classes.buttonTextDisabled
                  : classes.buttonText
              }
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

function ScarletWellCheckIn() {
  const classes = useScarletWellCheckInStyles()
  const theme = useTheme()
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'))
  const history = useHistory()
  const dispatch = useDispatch()
  const surveyData = useSelector(selectScarletWellData)
  const status = useSelector(selectScarletWellStatus)
  const drawerIsOpen = useSelector((state) => state.navigation.drawerIsOpen)
  const [selectedCategoryId, setSelectedCategoryId] = React.useState(null)
  const [selectedAnswers, setSelectedAnswers] = React.useState({})
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [needsFocus, setNeedsFocus] = React.useState(false)

  // Memoize categories payload to prevent stable array reference loss
  const categories = React.useMemo(
    () => surveyData?.checkInPage?.categories || [],
    [surveyData?.checkInPage?.categories],
  )

  // Wrap invalid categories calculation in useMemo so it only computes when answers or categories change
  const invalidCategoryIds = React.useMemo(
    () => getIncompleteCategoryIds(categories, selectedAnswers),
    [categories, selectedAnswers],
  )

  const handleCloseModal = React.useCallback(() => {
    handleCloseModalHelper({ setIsModalOpen, history })
  }, [history])

  const handleScrollToTop = React.useCallback(
    (shouldFocus = true) => {
      handleScrollToTopHelper(shouldFocus, {
        categoryPanelClass: classes.categoryPanel,
        isMdDown,
        setNeedsFocus,
      })
    },
    [classes.categoryPanel, isMdDown],
  )

  const onPreviousCategory = React.useCallback(
    (event) => {
      onPreviousCategoryHelper(event, {
        categories,
        setSelectedCategoryId,
        scrollToTop: handleScrollToTop,
      })
    },
    [categories, handleScrollToTop],
  )

  const onNextCategory = React.useCallback(
    (event) => {
      onNextCategoryHelper(event, {
        categories,
        setSelectedCategoryId,
        scrollToTop: handleScrollToTop,
      })
    },
    [categories, handleScrollToTop],
  )

  const handleSubmit = React.useCallback(async () => {
    await handleSubmitHelper({
      categories,
      setHasAttemptedSubmit,
      invalidCategoryIds,
      setSelectedCategoryId,
      setIsSubmitting,
      selectedAnswers,
      dispatch,
      submitAllAnswers,
      setIsModalOpen,
      setSelectedAnswers,
    })
  }, [categories, invalidCategoryIds, selectedAnswers, dispatch])

  // Wait for React to render the new category, then focus if needed
  React.useEffect(() => {
    if (needsFocus) {
      const firstRadioInput = document.querySelector(
        `.${classes.questionTable} input[type="radio"]`,
      )
      if (firstRadioInput) {
        // Use preventScroll: true so we don't jump away from the window scroll
        firstRadioInput.focus({ preventScroll: true })
        setNeedsFocus(false) // Reset flag after focusing
      }
    }
  }, [selectedCategoryId, needsFocus, classes.questionTable])

  React.useEffect(() => {
    const fetchSurveyData = async () => {
      try {
        await Promise.all([dispatch(fetchScarletWellCheckInData())])
      } catch (err) {
        console.log('Error fetching Scarlet Well Data', err)
      }
    }
    fetchSurveyData()
  }, [dispatch])

  React.useEffect(() => {
    if (
      surveyData &&
      surveyData?.checkInPage?.categories &&
      surveyData?.checkInPage?.categories.length > 0 &&
      selectedCategoryId == null
    ) {
      setSelectedCategoryId(surveyData.checkInPage.categories[0].categoryId)
    }
  }, [surveyData, selectedCategoryId])

  return (
    <Box
      className={
        drawerIsOpen ? classes.centerPageDrawerOpen : classes.centerPage
      }
      id="main-content"
    >
      <Box>
        <BackButtonNavBar
          onClick={() => history.push('/')}
          name="Back"
          topMargin={{ xs: 0, sm: 0, md: 64, lg: 64 }}
        />
      </Box>
      <Box className={classes.pageContainer}>
        {renderPageState(
          status,
          surveyData,
          selectedCategoryId,
          selectedAnswers,
          invalidCategoryIds,
          hasAttemptedSubmit,
          isSubmitting,
          classes,
          setSelectedCategoryId,
          setSelectedAnswers,
          onPreviousCategory,
          onNextCategory,
          handleSubmit,
        )}
      </Box>
      <Modal
        open={isModalOpen}
        onCloseDialog={handleCloseModal}
        dialogTitle="Submission Complete"
        dialogContent={
          <div className={classes.modalContent}>
            <Typography variant="h4">
              Thank you for completing the ScarletWell Wellness survey.
            </Typography>
            <br />
            <Typography variant="h5">
              View your latest scores on the widget.
            </Typography>
            <Typography variant="h5">
              Don&apos;t forget to check in regularly.
            </Typography>
          </div>
        }
        dialogActions={
          <Button
            color="primary"
            variant="contained"
            onClick={handleCloseModal}
          >
            Close
          </Button>
        }
      />
    </Box>
  )
}

export default ScarletWellCheckIn
